"use client";

import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  ThemeIcon,
  List,
  Alert,
  Divider,
  ColorSwatch,
  Paper,
  Anchor,
} from "@mantine/core";
import {
  IconPalette,
  IconClock,
  IconCheck,
  IconArrowRight,
  IconPhoto,
  IconShirt,
  IconSparkles,
  IconUsers,
  IconStar,
  IconEye,
  IconHeart,
  IconUpload,
  IconShield,
  IconDownload,
  IconDroplet,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { PRICING } from "@/constants/pricing";
import UnifiedUpsell from "@/components/UnifiedUpsell";
import EmailResultsCapture from "@/components/EmailResultsCapture";
import QuizPhotoUpload from "@/components/QuizPhotoUpload";
import { useTranslations } from "next-intl";
import { trackCtaClick } from "@/lib/analytics";

export default function SkinToneTestContent() {
  const t = useTranslations("skinToneTest");

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            {t("hero.title")}
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            {t("hero.subtitle")}
          </Text>

          <Button
            size="xl"
            component={Link}
            href="/undertone-quiz"
            color="violet"
            rightSection={<IconDroplet size={20} />}
            mb="xl"
          >
            {t("hero.button")}
          </Button>
        </Box>

        {/* Undertone vs overtone */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("undertoneVsOvertone.title")}
          </Title>
          <Text mb="lg">
            {t("undertoneVsOvertone.description")}
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="blue">
                {t("undertoneVsOvertone.undertoneTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("undertoneVsOvertone.undertoneDesc")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("undertoneVsOvertone.undertone1")}</List.Item>
                <List.Item>{t("undertoneVsOvertone.undertone2")}</List.Item>
                <List.Item>{t("undertoneVsOvertone.undertone3")}</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="orange">
                {t("undertoneVsOvertone.overtoneTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("undertoneVsOvertone.overtoneDesc")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("undertoneVsOvertone.overtone1")}</List.Item>
                <List.Item>{t("undertoneVsOvertone.overtone2")}</List.Item>
                <List.Item>{t("undertoneVsOvertone.overtone3")}</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Quick tests you can do */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("quickTests.title")}
          </Title>
          <Text mb="lg">{t("quickTests.description")}</Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            <Box>
              <ThemeIcon size={60} radius="xl" color="blue" variant="light" mb="md">
                <IconEye size={30} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="sm">
                {t("quickTests.veinTest")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("quickTests.veinDesc")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("quickTests.vein1")}</List.Item>
                <List.Item>{t("quickTests.vein2")}</List.Item>
                <List.Item>{t("quickTests.vein3")}</List.Item>
              </List>
            </Box>
            <Box>
              <ThemeIcon size={60} radius="xl" color="yellow" variant="light" mb="md">
                <IconStar size={30} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="sm">
                {t("quickTests.jewelryTest")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("quickTests.jewelryDesc")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("quickTests.jewelry1")}</List.Item>
                <List.Item>{t("quickTests.jewelry2")}</List.Item>
                <List.Item>{t("quickTests.jewelry3")}</List.Item>
              </List>
            </Box>
            <Box>
              <ThemeIcon size={60} radius="xl" color="green" variant="light" mb="md">
                <IconPalette size={30} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="sm">
                {t("quickTests.paperTest")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("quickTests.paperDesc")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("quickTests.paper1")}</List.Item>
                <List.Item>{t("quickTests.paper2")}</List.Item>
                <List.Item>{t("quickTests.paper3")}</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Lighting tips */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("lighting.title")}
          </Title>
          <Text mb="lg">{t("lighting.description")}</Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="green">
                ✅ {t("lighting.bestTitle")}
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>{t("lighting.best1")}</List.Item>
                <List.Item>{t("lighting.best2")}</List.Item>
                <List.Item>{t("lighting.best3")}</List.Item>
                <List.Item>{t("lighting.best4")}</List.Item>
                <List.Item>{t("lighting.best5")}</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="red">
                ❌ {t("lighting.avoidTitle")}
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>{t("lighting.avoid1")}</List.Item>
                <List.Item>{t("lighting.avoid2")}</List.Item>
                <List.Item>{t("lighting.avoid3")}</List.Item>
                <List.Item>{t("lighting.avoid4")}</List.Item>
                <List.Item>{t("lighting.avoid5")}</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Next: find your season */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("nextSteps.title")}
          </Title>
          <Text mb="lg">
            {t("nextSteps.description")}
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="blue">
                {t("nextSteps.coolTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("nextSteps.coolDesc")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("nextSteps.cool1")}</List.Item>
                <List.Item>{t("nextSteps.cool2")}</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="orange">
                {t("nextSteps.warmTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("nextSteps.warmDesc")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("nextSteps.warm1")}</List.Item>
                <List.Item>{t("nextSteps.warm2")}</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* FAQ */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("faq.title")}
          </Title>
          <Stack gap="md">
            <Box>
              <Title order={3} size="h5" mb="sm">
                {t("faq.q1")}
              </Title>
              <Text size="sm" c="dimmed">
                {t("faq.a1")}
              </Text>
            </Box>
            <Divider />
            <Box>
              <Title order={3} size="h5" mb="sm">
                {t("faq.q2")}
              </Title>
              <Text size="sm" c="dimmed">
                {t("faq.a2")}
              </Text>
            </Box>
            <Divider />
            <Box>
              <Title order={3} size="h5" mb="sm">
                {t("faq.q3")}
              </Title>
              <Text size="sm" c="dimmed">
                {t("faq.a3")}
              </Text>
            </Box>
            <Divider />
            <Box>
              <Title order={3} size="h5" mb="sm">
                {t("faq.q4")}
              </Title>
              <Text size="sm" c="dimmed">
                {t("faq.a4")}
              </Text>
            </Box>
          </Stack>
        </Card>

        {/* Color Recommendations by Undertone */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Best Colors for Each Skin Undertone
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Discover which colors will make you look radiant based on your skin undertone
          </Text>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
            <Box>
              <Title order={3} size="h4" mb="md" c="orange">
                Warm Undertones
              </Title>
              <Text mb="md" size="sm" c="dimmed">
                Colors that complement golden, peachy, or yellow undertones
              </Text>

              <Box mb="md">
                <Text fw={600} size="sm" mb="xs">
                  Best Clothing Colors:
                </Text>
                <Group gap="xs" mb="md">
                  <ColorSwatch color="#FF8C00" size={30} />
                  <ColorSwatch color="#FFD700" size={30} />
                  <ColorSwatch color="#CD853F" size={30} />
                  <ColorSwatch color="#8B4513" size={30} />
                  <ColorSwatch color="#32CD32" size={30} />
                </Group>
                <Text size="xs" c="dimmed">
                  Oranges, golds, warm browns, olive greens
                </Text>
              </Box>

              <Box mb="md">
                <Text fw={600} size="sm" mb="xs">
                  Best Makeup Colors:
                </Text>
                <List size="xs" spacing="xs">
                  <List.Item>Warm peach or golden foundation</List.Item>
                  <List.Item>Copper or bronze eyeshadows</List.Item>
                  <List.Item>Coral or warm pink lipsticks</List.Item>
                  <List.Item>Warm brown or gold eyeliners</List.Item>
                </List>
              </Box>
            </Box>

            <Box>
              <Title order={3} size="h4" mb="md" c="blue">
                Cool Undertones
              </Title>
              <Text mb="md" size="sm" c="dimmed">
                Colors that complement pink, red, or blue undertones
              </Text>

              <Box mb="md">
                <Text fw={600} size="sm" mb="xs">
                  Best Clothing Colors:
                </Text>
                <Group gap="xs" mb="md">
                  <ColorSwatch color="#4169E1" size={30} />
                  <ColorSwatch color="#8A2BE2" size={30} />
                  <ColorSwatch color="#20B2AA" size={30} />
                  <ColorSwatch color="#708090" size={30} />
                  <ColorSwatch color="#FF69B4" size={30} />
                </Group>
                <Text size="xs" c="dimmed">
                  Blues, purples, cool grays, bright pinks
                </Text>
              </Box>

              <Box mb="md">
                <Text fw={600} size="sm" mb="xs">
                  Best Makeup Colors:
                </Text>
                <List size="xs" spacing="xs">
                  <List.Item>Cool pink or neutral foundation</List.Item>
                  <List.Item>Cool purples or blues eyeshadows</List.Item>
                  <List.Item>Cool pink or berry lipsticks</List.Item>
                  <List.Item>Navy or cool brown eyeliners</List.Item>
                </List>
              </Box>
            </Box>

            <Box>
              <Title order={3} size="h4" mb="md" c="gray">
                Neutral Undertones
              </Title>
              <Text mb="md" size="sm" c="dimmed">
                Colors that work well with balanced warm and cool undertones
              </Text>

              <Box mb="md">
                <Text fw={600} size="sm" mb="xs">
                  Best Clothing Colors:
                </Text>
                <Group gap="xs" mb="md">
                  <ColorSwatch color="#808080" size={30} />
                  <ColorSwatch color="#A0522D" size={30} />
                  <ColorSwatch color="#2F4F4F" size={30} />
                  <ColorSwatch color="#8B0000" size={30} />
                  <ColorSwatch color="#556B2F" size={30} />
                </Group>
                <Text size="xs" c="dimmed">
                  Muted colors, balanced tones, sophisticated neutrals
                </Text>
              </Box>

              <Box mb="md">
                <Text fw={600} size="sm" mb="xs">
                  Best Makeup Colors:
                </Text>
                <List size="xs" spacing="xs">
                  <List.Item>Neutral or balanced foundation</List.Item>
                  <List.Item>Muted or balanced eyeshadows</List.Item>
                  <List.Item>Neutral or balanced lipsticks</List.Item>
                  <List.Item>Neutral or balanced eyeliners</List.Item>
                </List>
              </Box>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Common Mistakes */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Common Skin Tone Test Mistakes to Avoid
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Learn from these common mistakes to get the most accurate skin tone analysis
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box>
              <Title order={3} size="h4" mb="md" c="red">
                ❌ What Not to Do
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>Testing in artificial lighting (fluorescent, LED)</List.Item>
                <List.Item>Wearing makeup or self-tanner</List.Item>
                <List.Item>Testing on tanned skin (test on untanned areas)</List.Item>
                <List.Item>Using colored clothing that affects the test</List.Item>
                <List.Item>Testing in poor lighting or shadows</List.Item>
                <List.Item>Rushing through the tests</List.Item>
                <List.Item>Testing only one area of your body</List.Item>
                <List.Item>Ignoring seasonal changes in your skin</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md" c="green">
                ✅ Best Practices
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>Test in natural daylight near a window</List.Item>
                <List.Item>Remove all makeup and skincare products</List.Item>
                <List.Item>Test on clean, bare skin</List.Item>
                <List.Item>Wear neutral-colored clothing</List.Item>
                <List.Item>Take your time with each test</List.Item>
                <List.Item>Test multiple areas of your body</List.Item>
                <List.Item>Test on untanned areas (inner arm, chest)</List.Item>
                <List.Item>Consider your natural hair and eye color</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Professional vs DIY Analysis */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Professional vs DIY Skin Tone Analysis
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Understanding the differences between professional analysis and DIY methods
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box>
              <Title order={3} size="h4" mb="md" c="blue">
                Professional Analysis
              </Title>
              <Text mb="md">
                Professional color analysts use specialized tools and extensive training to determine your undertone.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Uses professional color drapes</List.Item>
                <List.Item>Tests in controlled lighting</List.Item>
                <List.Item>Considers multiple factors simultaneously</List.Item>
                <List.Item>Provides detailed explanations</List.Item>
                <List.Item>Can identify subtle undertone variations</List.Item>
                <List.Item>Costs $150-400 per session</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md" c="green">
                DIY Methods
              </Title>
              <Text mb="md">DIY methods are accessible and can be quite accurate when done correctly.</Text>
              <List size="sm" spacing="xs">
                <List.Item>Free and convenient</List.Item>
                <List.Item>Can be done at home</List.Item>
                <List.Item>Multiple tests for verification</List.Item>
                <List.Item>Good starting point for color analysis</List.Item>
                <List.Item>May need multiple attempts</List.Item>
                <List.Item>Results may be less precise</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Bridge to AI color analysis */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            {t("bridgeHeading")}
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            {t("bridgeBody")}
          </Text>
          <Box ta="center">
            <Button
              size="lg"
              component={Link}
              href="/ai-color-analysis?src=bridge_skintone"
              color="violet"
              rightSection={<IconDroplet size={20} />}
              onClick={() => trackCtaClick("bridge_skintone_ca")}
            >
              {t("bridgeButton")}
            </Button>
          </Box>
        </Card>

        {/* Bridge to virtual draping */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            See it on your own face
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Upload a selfie and our free virtual draping tool wraps you in gold vs silver, cream vs white, and warm vs cool red drapes — the same test analysts do in person. Free, no signup.
          </Text>
          <Box ta="center">
            <Button
              size="lg"
              variant="outline"
              component={Link}
              href="/virtual-color-draping?src=bridge_skintone"
              color="violet"
              rightSection={<IconDroplet size={20} />}
              onClick={() => trackCtaClick("bridge_skintone_vd")}
            >
              Drape yourself free
            </Button>
          </Box>
        </Card>

        {/* Related Tools */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Continue Your Color Journey
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Now that you understand your skin tone, explore these related tools
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            <Card withBorder padding="lg" radius="md" component={Link} href="/color-analysis-quiz" style={{ cursor: "pointer", textDecoration: "none" }} className="hover-lift">
              <ThemeIcon size={40} radius="xl" color="teal" variant="light" mb="md">
                <IconPalette size={20} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="xs">
                Color Analysis Quiz
              </Title>
              <Text size="sm" c="dimmed">
                Find your color season (Spring, Summer, Autumn, or Winter) and get your full 60+ color palette.
              </Text>
            </Card>
            <Card withBorder padding="lg" radius="md" component={Link} href="/gold-or-silver-jewelry-test" style={{ cursor: "pointer", textDecoration: "none" }} className="hover-lift">
              <ThemeIcon size={40} radius="xl" color="yellow" variant="light" mb="md">
                <IconStar size={20} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="xs">
                Gold or Silver Test
              </Title>
              <Text size="sm" c="dimmed">
                Discover whether gold or silver jewelry flatters your undertone better.
              </Text>
            </Card>
            <Card withBorder padding="lg" radius="md" component={Link} href="/style-personality-quiz" style={{ cursor: "pointer", textDecoration: "none" }} className="hover-lift">
              <ThemeIcon size={40} radius="xl" color="violet" variant="light" mb="md">
                <IconShirt size={20} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="xs">
                Style Personality Quiz
              </Title>
              <Text size="sm" c="dimmed">
                Find your fashion archetype: Classic, Romantic, Dramatic, Natural, or more.
              </Text>
            </Card>
          </SimpleGrid>
        </Card>

        {/* CSS for hover effects */}
        <style jsx global>{`
          .hover-lift:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
          .hover-lift {
            transition: transform 0.2s, box-shadow 0.2s;
          }
        `}</style>

        {/* Related undertone guides */}
        <Card withBorder p="lg" radius="md">
          <Title order={3} size="h5" mb="xs">
            Go deeper on undertones
          </Title>
          <Stack gap={6}>
            <Anchor component={Link} href="/cool-vs-warm-undertone-tests" size="sm">
              Cool vs. warm undertones: every test compared →
            </Anchor>
            <Anchor component={Link} href="/neutral-undertones-what-colors-suit-you" size="sm">
              Neutral undertones: what colors actually suit you →
            </Anchor>
            <Anchor component={Link} href="/chroma-value-brightness" size="sm">
              Chroma, value &amp; brightness explained →
            </Anchor>
          </Stack>
        </Card>

        <QuizPhotoUpload
          quizName="skin-tone-test"
          headline="See your exact colors — not just your undertone"
          subtext="Upload a photo and the AI confirms your full color season from your actual skin tone."
        />

        <EmailResultsCapture
          source="skin-tone-test"
          title="Get the free skin tone & color cheat sheet"
          subtitle="Undertone tricks, your likely season, and the colors to try first — straight to your inbox. Unsubscribe anytime."
        />

        <UnifiedUpsell
          title={t("upsell.title")}
          subtitle={t("upsell.subtitle")}
        />
      </Stack>
    </Container>
  );
}
